import { useState } from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '@/components/base/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ComicCard from '@/components/base/ComicCard';
import { categories, featuredComics, latestUpdates } from '@/mocks/zh/comics';

const allComics = [...featuredComics, ...latestUpdates].filter(
  (v, i, a) => a.findIndex((t) => t.id === v.id) === i
);

const categoryNames: Record<string, string> = {
  reboot: '热血',
  fantasy: '奇幻',
  romance: '恋爱',
  school: '校园',
  action: '格斗',
  sports: '运动',
  comedy: '喜剧',
  thriller: '悬疑',
};

const statusOptions = ['全部状态', '连载中', '已完结'];
const sortOptions = ['最新更新', '最受欢迎', '评分最高'];

const breadcrumbSchema = (categoryName: string) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: '首页', item: 'http://lczydmb.com/' },
    { '@type': 'ListItem', position: 2, name: '分类', item: 'http://lczydmb.com/category' },
    { '@type': 'ListItem', position: 3, name: categoryName, item: 'http://lczydmb.com/category/all' },
  ],
});

export default function CategoryPage() {
  const [selectedCat, setSelectedCat] = useState('reboot');
  const [selectedStatus, setSelectedStatus] = useState('全部状态');
  const [selectedSort, setSelectedSort] = useState('最新更新');

  const categoryName = categoryNames[selectedCat] || '分类';

  const filteredComics = allComics.filter((c) => {
    const cat = categories.find((x) => x.slug === selectedCat);
    if (!cat) return true;
    return c.category === cat.name;
  });

  if (selectedStatus !== '全部状态') {
    // filter by status would go here
  }

  return (
    <>
      <SEOHead
        title={`${categoryName}韩漫_${categoryName}韩国漫画在线免费阅读-韩漫网站`}
        description={`韩漫免费网站提供丰富${categoryName}类韩国漫画，高清无删减在线免费阅读。发现最热${categoryName}韩漫，每日同步更新最新章节。`}
        keywords={`${categoryName}韩漫,${categoryName}韩国漫画,韩漫分类,在线免费阅读`}
        canonicalUrl={'http://lczydmb.com/category/all'}
      />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema(categoryName)) }} />
      <Navbar />

      <main className="min-h-screen pt-16">
        <section className="bg-background-100 py-10 md:py-14 px-4 md:px-6">
          <div className="max-w-7xl mx-auto">
            <Breadcrumb items={[{ name: '首页', path: '/' }, { name: '分类', path: '/category/all' }, { name: categoryName }]} />

            <div className="mt-6 flex flex-col lg:flex-row gap-8">
              <div className="lg:w-[320px] flex-shrink-0">
                <div className="relative overflow-hidden rounded-2xl aspect-[4/5]">
                  <img
                    src={categories.find((c) => c.slug === selectedCat)?.cover || categories[0].cover}
                    alt={categoryName}
                    title={`${categoryName}韩漫 - 韩漫免费网站`}
                    className="w-full h-full object-cover object-top"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/60 to-transparent" />
                  <div className="absolute top-4 left-4 flex gap-2 flex-wrap">
                    <span className="bg-background-50/20 backdrop-blur-sm text-background-50 text-xs px-3 py-1 rounded-full font-label whitespace-nowrap">
                      热门
                    </span>
                    <span className="bg-background-50/20 backdrop-blur-sm text-background-50 text-xs px-3 py-1 rounded-full font-label whitespace-nowrap">
                      推荐
                    </span>
                    <span className="bg-background-50/20 backdrop-blur-sm text-background-50 text-xs px-3 py-1 rounded-full font-label whitespace-nowrap">
                      精选
                    </span>
                  </div>
                </div>
              </div>

              <div className="flex-1 flex flex-col justify-between">
                <div>
                  <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-950">{categoryName}</h1>
                  <div className="flex items-center gap-3 mt-3 flex-wrap">
                    {statusOptions.map((opt) => (
                      <button
                        key={opt}
                        onClick={() => setSelectedStatus(opt)}
                        className={`px-4 py-1.5 rounded-full text-sm font-label whitespace-nowrap cursor-pointer transition-all duration-200 ${
                          selectedStatus === opt
                            ? 'bg-primary-500 text-background-50'
                            : 'border border-foreground-300 text-foreground-600 hover:border-primary-300 hover:text-primary-500'
                        }`}
                      >
                        {opt}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="mt-6 bg-foreground-950 rounded-2xl p-6 flex flex-col sm:flex-row items-start sm:items-center gap-4">
                  <div className="flex-1">
                    <span className="text-xs font-label text-accent-400 uppercase">精选推荐</span>
                    <h2 className="text-lg font-heading font-semibold text-background-50 mt-1">
                      {filteredComics[0]?.title || '精选韩漫'}
                    </h2>
                    <p className="text-sm text-foreground-400 mt-2 line-clamp-2">
                      {filteredComics[0]?.description || '发现最精彩的韩国漫画'}
                    </p>
                  </div>
                  {filteredComics[0] && (
                    <Link
                      to="/category/all"
                      className="bg-accent-500 hover:bg-accent-400 text-foreground-950 px-5 py-2.5 rounded-full text-sm font-label whitespace-nowrap cursor-pointer transition-colors duration-200 flex-shrink-0"
                    >
                      <i className="ri-play-circle-line mr-1.5" />
                      立即阅读
                    </Link>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-10 md:py-14 px-4 md:px-6 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
              <div>
                <span className="text-xs font-label text-primary-500 uppercase tracking-wider">作品列表</span>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mt-1">
                  全部<strong className="text-primary-500">{categoryName}</strong>韩漫
                </h2>
              </div>
              <div className="flex items-center gap-2">
                {sortOptions.map((opt) => (
                  <button
                    key={opt}
                    onClick={() => setSelectedSort(opt)}
                    className={`px-3 py-1.5 rounded-full text-xs font-label whitespace-nowrap cursor-pointer transition-all duration-200 ${
                      selectedSort === opt
                        ? 'bg-secondary-500 text-background-50'
                        : 'text-foreground-600 hover:text-foreground-900'
                    }`}
                  >
                    {opt}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-6">
              {filteredComics.map((comic) => (
                <ComicCard
                  key={comic.id}
                  id={comic.id}
                  title={comic.title}
                  author={comic.author}
                  category={comic.category}
                  cover={comic.cover}
                  rating={comic.rating}
                  status={comic.status}
                  latestChapter={comic.latestChapter}
                  views={comic.views}
                />
              ))}
            </div>
          </div>
        </section>

        <section className="py-10 md:py-14 px-4 md:px-6 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-6">
              更多<strong className="text-accent-500">分类</strong>
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {categories.map((cat) => (
                <button
                  key={cat.slug}
                  onClick={() => setSelectedCat(cat.slug)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-label whitespace-nowrap cursor-pointer transition-all duration-200 ${
                    selectedCat === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-50 text-foreground-700 hover:bg-background-200'
                  }`}
                >
                  <span className="text-base">{cat.name}</span>
                  <span className="text-xs opacity-60">{cat.count}</span>
                </button>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}